import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // Changed useParams to useLocation
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../firebase';
import './IsiLogbookInstrumen.css'; // CSS for styling

const IsiLogbookInstrumen = () => {
  // const { instrumentId } = useParams(); // No longer using URL params
  const location = useLocation(); // Use location to get state
  const navigate = useNavigate();
  const auth = getAuth();

  // Extract data from location state
  const { alatId, namaAlat, permintaanId } = location.state || {}; // Destructure with default empty object

  // const [instrument, setInstrument] = useState(null); // No need to fetch instrument details if name is passed
  const [loading, setLoading] = useState(false); // Keep loading for user fetch maybe? Or remove if not needed.
  const [error, setError] = useState('');
  const [user, setUser] = useState(null); // Store user info (name, nim)
  const [logData, setLogData] = useState({
    activity: '',
    conditionBefore: '',
    conditionAfter: '',
    tanggalMulai: '', // Added
    jamMulai: '',     // Added
    tanggalSelesai: '',// Added
    jamSelesai: '',   // Added
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestData, setRequestData] = useState(null); // Store fetched request data

  // Validate navigation state on mount
  useEffect(() => {
    if (!alatId || !namaAlat || !permintaanId) {
      setError('Data alat atau permintaan tidak valid. Silakan akses melalui menu "Alat Siap Digunakan" atau QR Scanner.');
      // Optionally redirect back after a delay
      // setTimeout(() => navigate('/mahasiswa/alat-siap-digunakan'), 3000);
    }
  }, [alatId, namaAlat, permintaanId, navigate]);


  // Fetch user details
  useEffect(() => {
    setLoading(true); // Start loading for user fetch
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Fetch user details from Firestore 'users' collection
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUser({ uid: currentUser.uid, ...userSnap.data() });
        } else {
          setError('Gagal mengambil data pengguna.');
          setUser(null); // Ensure user is null if data fetch fails
        }
      } else {
        setError('Silakan login untuk mengisi logbook.');
        setUser(null);
        // Optionally redirect to login
        // navigate('/login');
      }
      setLoading(false); // Stop loading after user fetch attempt
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  // Fetch the corresponding loan request details
  useEffect(() => {
    const fetchRequestData = async () => {
      if (!permintaanId) return; // Don't fetch if ID is missing

      try {
        const requestRef = doc(db, 'permintaan_alat', permintaanId);
        const requestSnap = await getDoc(requestRef);
        if (requestSnap.exists()) {
          setRequestData(requestSnap.data());
        } else {
          console.warn(`Request document with ID ${permintaanId} not found.`);
          // Optionally set an error state here if this is critical
        }
      } catch (err) {
        console.error("Error fetching request data:", err);
        // Optionally set an error state
      }
    };

    fetchRequestData();
  }, [permintaanId]); // Fetch when permintaanId is available


  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setLogData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- Validation Checks ---
    if (!alatId || !namaAlat || !permintaanId) {
       setError('Data alat atau permintaan tidak valid. Tidak dapat menyimpan logbook.');
       return;
    }
    if (!user) {
      setError('Data pengguna tidak tersedia. Tidak dapat menyimpan logbook.');
      return;
    }
    if (!logData.activity || !logData.conditionBefore || !logData.conditionAfter ||
        !logData.tanggalMulai || !logData.jamMulai || !logData.tanggalSelesai || !logData.jamSelesai) {
      setError('Semua field wajib diisi, termasuk tanggal dan jam mulai/selesai.');
      return;
    }
    // Check if request data (loan period) is loaded
    if (!requestData) {
      setError('Data periode peminjaman belum dimuat. Silakan coba lagi.');
      return;
    }

    // Combine logbook date/time and validate against loan period
    const logStartDateTime = new Date(`${logData.tanggalMulai}T${logData.jamMulai}`);
    const logEndDateTime = new Date(`${logData.tanggalSelesai}T${logData.jamSelesai}`);
    const loanStartDateTime = requestData.tanggalPinjam?.toDate(); // Use optional chaining
    const loanEndDateTime = requestData.tanggalKembali?.toDate(); // Use optional chaining

    if (isNaN(logStartDateTime.getTime()) || isNaN(logEndDateTime.getTime())) {
        setError('Format tanggal atau waktu logbook tidak valid.');
        return;
    }
     if (!loanStartDateTime || !loanEndDateTime) {
        setError('Periode peminjaman tidak valid. Hubungi admin.');
        return;
    }
    if (logEndDateTime <= logStartDateTime) {
        setError('Waktu selesai logbook harus setelah waktu mulai.');
        return;
    }

    // --- MODIFIED CHECK: Validate dates only ---
    // Create date-only versions for comparison (set time to midnight)
    const logStartDate = new Date(logStartDateTime);
    logStartDate.setHours(0, 0, 0, 0);
    const logEndDate = new Date(logEndDateTime);
    logEndDate.setHours(0, 0, 0, 0);
    const loanStartDate = new Date(loanStartDateTime);
    loanStartDate.setHours(0, 0, 0, 0);
    const loanEndDate = new Date(loanEndDateTime);
    loanEndDate.setHours(0, 0, 0, 0);

    // Check if log dates are within the loan dates (inclusive)
    if (logStartDate < loanStartDate || logEndDate > loanEndDate) {
      // Updated error message to reflect date-only validation
      setError(`Tanggal logbook (Mulai: ${logStartDate.toLocaleDateString('id-ID')}, Selesai: ${logEndDate.toLocaleDateString('id-ID')}) harus berada dalam periode tanggal peminjaman yang disetujui (Mulai: ${loanStartDate.toLocaleDateString('id-ID')}, Selesai: ${loanEndDate.toLocaleDateString('id-ID')}). Waktu spesifik dalam hari tersebut tidak dibatasi.`);
      return;
    }
    // --- End MODIFIED CHECK ---

    // --- End Validation Checks ---

    setIsSubmitting(true);
    setError('');

    // Prepare base log data
    const logEntryBase = {
      instrumentId: alatId,
      instrumentName: namaAlat,
      permintaanId: permintaanId,
      userId: user.uid,
      userName: user.nama || user.email || 'N/A',
      userNim: user.nim || 'N/A',
      userProgramStudi: user.programStudi || 'N/A',
      activity: logData.activity,
      conditionBefore: logData.conditionBefore,
      conditionAfter: logData.conditionAfter,
      tanggalMulai: logData.tanggalMulai,
      jamMulai: logData.jamMulai,
      tanggalSelesai: logData.tanggalSelesai,
      jamSelesai: logData.jamSelesai,
      logTimestamp: serverTimestamp(),
    };

    // Add actual return time if available from the fetched request
    if (requestData?.tanggalDikembalikanActual) {
      logEntryBase.tanggalDikembalikanActual = requestData.tanggalDikembalikanActual;
    }

    try {
      // Save the complete log entry data - Corrected addDoc call
      await addDoc(collection(db, 'logbook_instrumen'), logEntryBase);

      // Removed duplicate data object that was incorrectly inserted

      alert('Logbook berhasil disimpan!');
      // Navigate back to the list of usable tools or dashboard
      navigate('/mahasiswa/alat-siap-digunakan');
    } catch (err) {
      console.error("Error saving logbook:", err);
      setError(`Gagal menyimpan logbook: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading only while fetching user data initially
  if (loading && !user) return <div className="loading-message">Memuat data pengguna...</div>;
  // Show error if critical data is missing
  if (!alatId || !namaAlat || !permintaanId) return <div className="error-message">{error || 'Data alat tidak lengkap.'}</div>;


  return (
    <div className="logbook-form-container">
      {/* Use namaAlat from state */}
      <h2>Logbook Instrumen: {namaAlat}</h2>
      {error && <div className="error-message">{error}</div>} {/* Show non-critical errors */}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="activity">Kegiatan yang Dilakukan:</label>
          <textarea
            id="activity"
            name="activity"
            value={logData.activity}
            onChange={handleChange}
            required
            rows="3"
          />
        </div>
        <div className="form-group">
          <label htmlFor="conditionBefore">Kondisi Alat Sebelum Digunakan:</label>
          <input
            type="text"
            id="conditionBefore"
            name="conditionBefore"
            value={logData.conditionBefore}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="conditionAfter">Kondisi Alat Sesudah Digunakan:</label>
          <input
            type="text"
            id="conditionAfter"
            name="conditionAfter"
            value={logData.conditionAfter}
            onChange={handleChange}
            required
          />
        </div>
        {/* Added Date and Time Fields */}
        <div className="form-group form-row"> {/* Use form-row for side-by-side layout */}
          <div className="form-column">
            <label htmlFor="tanggalMulai">Tanggal Mulai:</label>
            <input
              type="date"
              id="tanggalMulai"
              name="tanggalMulai"
              value={logData.tanggalMulai}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-column">
            <label htmlFor="jamMulai">Jam Mulai:</label>
            <input
              type="time"
              id="jamMulai"
              name="jamMulai"
              value={logData.jamMulai}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className="form-group form-row">
          <div className="form-column">
            <label htmlFor="tanggalSelesai">Tanggal Selesai:</label>
            <input
              type="date"
              id="tanggalSelesai"
              name="tanggalSelesai"
              value={logData.tanggalSelesai}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-column">
            <label htmlFor="jamSelesai">Jam Selesai:</label>
            <input
              type="time"
              id="jamSelesai"
              name="jamSelesai"
              value={logData.jamSelesai}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <button type="submit" disabled={isSubmitting || !user} className="submit-button">
          {isSubmitting ? 'Menyimpan...' : 'Simpan Logbook'}
        </button>
        {!user && !loading && <p style={{color: 'red', marginTop: '10px'}}>Gagal memuat data pengguna. Silakan coba lagi.</p>}
      </form>
    </div>
  );
};

export default IsiLogbookInstrumen;
